/*
 * FICHIER: Footer.js
 * BUT: Composant Footer (pied de page) de l'application OffiSport
 * 
 * FONCTIONNALITÉS :
 * - Navigation vers toutes les sections principales
 * - Informations de contact (adresse, téléphone, email)
 * - Liens vers les réseaux sociaux
 * - Newsletter d'inscription
 * - Liens légaux (mentions légales, politique de confidentialité)
 * - Ruban des partenaires
 * - Design responsive complet
 * 
 * ARCHITECTURE :
 * - Grille responsive à 5 colonnes (3 sur tablette, 1 sur mobile)
 * - Utilise les variables CSS globales pour la cohérence
 * - Composants styled-components pour le styling
 * - Navigation via React Router
 * 
 * SECTIONS DU FOOTER :
 * 1. Logo et description
 * 2. Navigation principale
 * 3. Informations de contact
 * 4. Réseaux sociaux
 * 5. Newsletter
 */

// IMPORTS REACT ET HOOKS
import React, { useState } from 'react';

// IMPORT STYLED COMPONENTS POUR LE STYLING
import styled from 'styled-components';

// IMPORTS REACT ROUTER POUR LA NAVIGATION
import { Link, useNavigate, useLocation } from 'react-router-dom';

// IMPORTS REACT ICONS POUR LES ICÔNES
import { 
  FaFacebook,       // Facebook
  FaTwitter,        // Twitter
  FaInstagram,      // Instagram
  FaYoutube,        // YouTube
  FaLinkedin,       // LinkedIn
  FaMapMarkerAlt,   // Localisation
  FaPhoneAlt,       // Téléphone
  FaEnvelope,       // Email
  FaArrowRight,     // Flèche droite
  FaInfoCircle      // À propos
} from 'react-icons/fa';

// IMPORTS COMPOSANTS LOCAUX
import Logo from './Logo';                                    // Composant logo
import LegalPopup from '../popups/LegalPopup';                // Popup mentions légales
import AboutPopupSidebar from '../popups/AboutPopupSidebar';  // Popup À propos
import { useNavigation } from '../../contexts/NavigationContext'; // Contexte navigation
import PartnerRibbon from './PartnerRibbon';                  // Ruban partenaires

/*
 * =================================================================================
 * STYLED COMPONENTS - Composants stylés pour le footer
 * =================================================================================
 */

// CONTENEUR PRINCIPAL DU FOOTER
const FooterContainer = styled.footer`
  background-color: var(--gray-darker);     // Fond sombre (variable CSS globale)
  color: var(--white);                      // Texte blanc
  padding: var(--space-xl) 0 0;             // Padding en haut seulement
  position: relative;
  margin-top: var(--space-xxl);            // Espacement important avec le contenu principal
`;

// CONTENU PRINCIPAL DU FOOTER (GRILLE)
const FooterContent = styled.div`
  max-width: var(--container-xl);           // Largeur maximale (variable CSS)
  margin: 0 auto;                           // Centrage horizontal
  padding: 0 var(--space-lg);
  display: grid;
  grid-template-columns: repeat(5, 1fr);    // 5 colonnes égales par défaut
  gap: var(--space-xl);                     // Espacement entre colonnes

  /* RESPONSIVE - Écrans moyens */
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);  // 3 colonnes sur tablette
  }

  /* RESPONSIVE - Mobile */
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);  // 2 colonnes sur mobile
    padding: 0 var(--space-md);             // Padding réduit
    gap: var(--space-md);                    // Espacement réduit entre colonnes
  }

  /* RESPONSIVE - Très petits écrans */
  @media (max-width: 400px) {
    grid-template-columns: 1fr;             // 1 colonne sur très petits écrans
    gap: var(--space-sm);                    // Espacement encore plus réduit
  }
`;

// COLONNE INDIVIDUELLE DU FOOTER
const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;                   // Disposition verticale
  margin-bottom: var(--space-lg);
  flex: 1;                                  // Prend tout l'espace disponible
`;

// LOGO DANS LE FOOTER (LIEN VERS ACCUEIL)
const FooterLogo = styled(Link)`
  color: var(--white);
  font-size: var(--font-size-xl);
  font-weight: 700;                         // Police grasse
  margin-bottom: var(--space-md);
  display: inline-block;
  text-decoration: none;                    // Pas de soulignement

  /* STYLE POUR L'IMAGE DU LOGO */
  img {
    height: 45px;                           // Hauteur fixe
    margin-right: var(--space-xs);          // Espacement avec le texte
  }
`;

// PARAGRAPHE DE TEXTE DANS LE FOOTER
const FooterText = styled.p`
  margin-bottom: var(--space-md);
  font-size: var(--font-size-sm);          // Taille de police petite
  line-height: 1.7;                        // Interligne généreux pour la lisibilité
  color: var(--gray-light);                // Couleur grise claire
`;

// TITRE DE SECTION DANS LE FOOTER
const FooterTitle = styled.h4`
  color: #132e57;                          // Couleur bleue spécifique
  font-size: var(--font-size-md);
  font-weight: 600;                        // Police semi-grasse
  margin-bottom: var(--space-md);
  position: relative;                      // Pour le pseudo-élément
  padding-bottom: var(--space-sm);
  margin-top: 0;

  /* LIGNE DÉCORATIVE SOUS LE TITRE */
  &:after {
    content: '';                           // Pseudo-élément vide
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;                           // Largeur de la ligne
    height: 2px;                           // Épaisseur de la ligne
    background-color: var(--primary);     // Couleur primaire
  }
`;

// LISTE DE LIENS (RESET DES STYLES PAR DÉFAUT)
const FooterLinks = styled.ul`
  list-style: none;                        // Supprime les puces
  padding: 0;                              // Supprime le padding par défaut
  margin: 0;                               // Supprime la marge par défaut
`;

// ÉLÉMENT DE LISTE (CONTAINER POUR CHAQUE LIEN)
const FooterLink = styled.li`
  margin-bottom: var(--space-sm);          // Espacement entre les liens
`;

// LIEN STYLÉ AVEC ICÔNE ET ANIMATION
const StyledLink = styled(Link)`
  color: var(--gray-light);                // Couleur grise par défaut
  font-size: var(--font-size-sm);
  transition: color var(--transition-fast); // Animation de couleur
  display: flex;
  align-items: center;                     // Alignement vertical de l'icône et du texte
  text-decoration: none;                   // Pas de soulignement
  
  /* STYLE POUR LES ICÔNES SVG */
  svg {
    margin-right: var(--space-xs);         // Espacement avec le texte
    font-size: var(--font-size-sm);
    opacity: 0.7;                          // Semi-transparence par défaut
    transition: all var(--transition-fast); // Animation complète
  }
  
  /* EFFET HOVER */
  &:hover {
    color: var(--primary);                 // Couleur primaire au survol
    
    /* ANIMATION DE L'ICÔNE AU SURVOL */
    svg {
      opacity: 1;                          // Opacité complète
      transform: translateX(3px);          // Déplacement vers la droite
    }
  }
`;

// BLOC D'INFORMATIONS DE CONTACT
const ContactInfo = styled.div`
  display: flex;
  align-items: flex-start;                 // Alignement en haut (pour les textes multilignes)
  margin-bottom: var(--space-md);

  /* STYLE POUR L'ICÔNE */
  svg {
    color: var(--primary);                 // Couleur primaire pour l'icône
    font-size: var(--font-size-md);
    margin-right: var(--space-sm);         // Espacement avec le texte
    margin-top: 4px;                       // Léger décalage pour l'alignement
    min-width: 16px;                       // Largeur minimale pour éviter la compression
  }

  /* STYLE POUR LE TEXTE */
  div {
    color: var(--gray-light);              // Couleur grise claire
    font-size: var(--font-size-sm);
    line-height: 1.6;                      // Interligne pour la lisibilité
  }
`;

// CONTENEUR DES LIENS SOCIAUX
const SocialLinks = styled.div`
  display: flex;
  gap: var(--space-md);                    // Espacement entre les icônes
  margin-top: var(--space-md);
`;

// LIEN SOCIAL INDIVIDUEL (ICÔNE CIRCULAIRE)
const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;                             // Largeur fixe
  height: 45px;                            // Hauteur fixe (cercle parfait)
  border-radius: 50%;                      // Cercle parfait
  color: var(--white);
  background-color: rgba(255, 255, 255, 0.1); // Fond semi-transparent
  transition: all var(--transition-normal); // Animation complète
  font-size: 20px;

  /* EFFET HOVER */
  &:hover {
    background-color: var(--primary);      // Fond couleur primaire
    transform: translateY(-3px);           // Élévation légère
  }
`;

// SECTION NEWSLETTER
const Newsletter = styled.div`
  margin-top: var(--space-sm);
`;

// FORMULAIRE NEWSLETTER
const NewsletterForm = styled.form`
  display: flex;
  margin-top: var(--space-sm);
  position: relative;

  /* RESPONSIVE - Mobile */
  @media (max-width: 576px) {
    flex-direction: column;                // Disposition verticale sur mobile
  }
`;

// CHAMP DE SAISIE NEWSLETTER
const NewsletterInput = styled.input`
  flex: 1;                                 // Prend tout l'espace disponible
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--gray);
  border-radius: var(--radius-sm);
  background-color: rgba(255, 255, 255, 0.05); // Fond légèrement transparent
  color: var(--white);
  font-size: var(--font-size-sm);

  /* STYLE DU PLACEHOLDER */
  &::placeholder {
    color: var(--gray-light);              // Couleur grise pour le placeholder
  }

  /* RESPONSIVE - Mobile */
  @media (max-width: 576px) {
    margin-bottom: var(--space-sm);        // Espacement avec le bouton sur mobile
  }
`;

// BOUTON NEWSLETTER
const NewsletterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-sm) var(--space-md);
  background-color: var(--primary);       // Fond couleur primaire
  color: var(--white);
  border: none;                            // Pas de bordure
  border-radius: var(--radius-sm);
  font-weight: 500;                        // Police semi-grasse
  cursor: pointer;                         // Curseur pointer
  transition: all var(--transition-fast);  // Animation
  margin-left: var(--space-xs);

  /* STYLE POUR L'ICÔNE */
  svg {
    margin-left: var(--space-xs);          // Espacement avec le texte
  }

  /* EFFET HOVER */
  &:hover {
    background-color: var(--primary-dark); // Couleur plus sombre au survol
  }

  /* RESPONSIVE - Mobile */
  @media (max-width: 576px) {
    margin-left: 0;                        // Pas de marge sur mobile
  }
`;

// BAS DU FOOTER (COPYRIGHT ET INFOS LÉGALES)
const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1); // Bordure subtile en haut
  padding: var(--space-md) var(--space-lg);
  text-align: center;
  margin-top: var(--space-lg);
  font-size: var(--font-size-xs);          // Très petite taille
  color: var(--gray-light);
`;

const FooterCreditLink = styled.a`
  color: inherit;
  text-decoration: underline;
  transition: color var(--transition-fast);
  &:hover {
    color: var(--primary);
  }
`;

// LIEN RÉSEAU SOCIAL AVEC TEXTE (ALTERNATIVE AU STYLE CIRCULAIRE)
const SocialNetworkLink = styled.a`
  color: var(--gray-light);
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: var(--font-size-sm);
  transition: color var(--transition-fast);
  margin-bottom: 10px;
  
  /* STYLE POUR L'ICÔNE */
  svg {
    margin-right: var(--space-xs);
    font-size: var(--font-size-md);
    opacity: 0.7;
    transition: all var(--transition-fast);
  }
  
  /* EFFET HOVER */
  &:hover {
    color: var(--primary);
    
    svg {
      opacity: 1;
      transform: translateX(3px);
      color: var(--primary);
    }
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showLegalPopup, setShowLegalPopup] = useState(false);
  const [showAboutPopup, setShowAboutPopup] = useState(false);
  const [initialTab, setInitialTab] = useState('mentions-legales');
  const navigate = useNavigate();
  const location = useLocation();

  // Fonction pour déterminer quel type de partenaires afficher selon la page courante
  const getPartnerFilterType = () => {
    const currentPath = location.pathname;
    
    // Pages avec partenaires privées : Accueil, activité, biblio
    if (currentPath === '/' || 
        currentPath === '/accueil' || 
        currentPath.startsWith('/activites') || 
        currentPath.startsWith('/bibliotheque')) {
      return 'Privée';
    }
    
    // Pages avec partenaires publiques : Actualités, événement et club
    if (currentPath.startsWith('/actualites') || 
        currentPath.startsWith('/evenements') || 
        currentPath.startsWith('/clubs')) {
      return 'Publique';
    }
    
    // Page partenaires : mélanger les deux
    if (currentPath.startsWith('/partenaires')) {
      return 'all';
    }
    
    // Par défaut : tous les partenaires
    return 'all';
  };

  const handleLegalClick = (tab) => {
    setInitialTab(tab);
    setShowLegalPopup(true);
  };

  const handleSectionClick = (sectionId, e) => {
    e.preventDefault();
    const routeMap = {
      'accueil': '',
      'actualites': 'actualites',
      'activites': 'activites',
      'evenements': 'evenements',
      'clubs': 'clubs',
      'bibliotheque': 'bibliotheque',
      'partenaires': 'partenaires'
    };
    const route = routeMap[sectionId];
    if (route !== undefined) {
      navigate(`/${route}`);
      window.scrollTo(0,0);
    }
  };

  return (
    <FooterContainer>
      <div style={{ marginBottom: '24px' }}>
        <PartnerRibbon filterType={getPartnerFilterType()} />
      </div>
      <FooterContent>
        <FooterColumn>
          <FooterTitle>Liens Rapides</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('accueil', e)}>
                <FaArrowRight /> Accueil
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('actualites', e)}>
                <FaArrowRight /> Actualités
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('activites', e)}>
                <FaArrowRight /> Activités
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('evenements', e)}>
                <FaArrowRight /> Événements
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('clubs', e)}>
                <FaArrowRight /> Clubs
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('bibliotheque', e)}>
                <FaArrowRight /> Bibliothèque
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink onClick={(e) => handleSectionClick('partenaires', e)}>
                <FaArrowRight /> Partenaires
              </StyledLink>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Contactez-nous</FooterTitle>
          <ContactInfo>
            <FaMapMarkerAlt />
            <div>Rue Albert Robida<br />60200 Compiègne, France</div>
          </ContactInfo>
          <ContactInfo>
            <FaPhoneAlt />
            <div>+33 6 52 94 92 82</div>
          </ContactInfo>
          <ContactInfo>
            <FaEnvelope />
            <div onClick={() => handleLegalClick('contact')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
              contact@offisport.com
            </div>
          </ContactInfo>
          <ContactInfo
            onClick={() => setShowAboutPopup(true)}
            style={{ cursor: 'pointer' }}
          >
            <FaInfoCircle />
            <div>À propos de nous</div>
          </ContactInfo>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Réseaux</FooterTitle>
          <SocialNetworkLink href="https://www.facebook.com/oscar.osarc.9" target="_blank" rel="noopener noreferrer">
            <FaFacebook /> Facebook
          </SocialNetworkLink>
          <SocialNetworkLink href="https://www.instagram.com/offisport/" target="_blank" rel="noopener noreferrer">
            <FaInstagram /> Instagram
          </SocialNetworkLink>
          <SocialNetworkLink href="https://www.linkedin.com/company/office-des-sports-de-l-arc-osarc/" target="_blank" rel="noopener noreferrer">
            <FaLinkedin /> LinkedIn
          </SocialNetworkLink>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Horaires d'ouverture</FooterTitle>
          <FooterText>
            <strong>Lundi - Vendredi:</strong> 9h - 17h30<br />
            <strong>Samedi:</strong> Fermé <br />
            <strong>Dimanche:</strong> Fermé
          </FooterText>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>Légal</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <StyledLink to="#" onClick={(e) => { e.preventDefault(); handleLegalClick('mentions-legales'); }}>
                <FaArrowRight /> Mentions légales
              </StyledLink>
            </FooterLink>
            <FooterLink>
              <StyledLink to="#" onClick={(e) => { e.preventDefault(); handleLegalClick('confidentialite'); }}>
                <FaArrowRight /> Politique de confidentialité
              </StyledLink>
            </FooterLink>            
            <FooterLink>
              <StyledLink to="#" onClick={(e) => { e.preventDefault(); handleLegalClick('licence'); }}>
                <FaArrowRight /> Licence d'utilisation
              </StyledLink>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>
      </FooterContent>
      
      <FooterBottom>
        <p>&copy; {currentYear} OffiSport. Tous droits réservés. Site conçu par <FooterCreditLink href="https://glweb.fr/" target="_blank" rel="noopener noreferrer">GLWeb</FooterCreditLink>.</p>
      </FooterBottom>

      {showLegalPopup && (
        <LegalPopup onClose={() => setShowLegalPopup(false)} initialTab={initialTab} />
      )}

      <AboutPopupSidebar 
        isOpen={showAboutPopup} 
        onClose={() => setShowAboutPopup(false)} 
      />
    </FooterContainer>
  );
};

export default Footer; 